import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUboList, Ubo } from "../lib/api";

function fmtDate(d?: string | null) {
  if (!d) return "—";
  const only = d.split("T")[0] || d;
  return only;
}

function renderLivenessChip(u: Ubo) {
  const snap: any = u.kyc_liveness;
  if (!snap || typeof snap !== "object") return null;

  const ok = !!(snap.ok ?? snap.pass);
  const label: string = (snap.label ||
    snap.selfie_label ||
    snap.mrz_label ||
    (ok ? "PASS" : "CHECK")) as string;
  const spoof =
    typeof snap.spoof_score === "number"
      ? (snap.spoof_score as number)
      : undefined;

  const cls = ok ? "chip chip-success" : "chip chip-danger";
  const textParts = [`KYC / Liveness: ${label}`];
  if (spoof !== undefined) {
    textParts.push(`spoof=${spoof.toFixed(2)}`);
  }

  return <span className={cls}>{textParts.join(" · ")}</span>;
}

export default function UboIndex() {
  const [items, setItems] = useState<Ubo[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiUboList();
        setItems(data || []);
      } catch (e: any) {
        setErr(e.message || "Failed to load UBOs");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-4">Loading…</div>;
  if (err) return <div className="p-4 text-red-600">{err}</div>;

  return (
    <div
      className="space-y-4 p-4 sm:p-0"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)" }}
    >
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Ultimate Beneficial Owners (UBOs)</h1>
        {/* FIX: route under /profile/ubos/... */}
        <Link to="/profile/ubos/create" className="btn btn-white">
          + Add UBO
        </Link>
      </div>

      {/* Mobile: card list */}
      <div className="grid gap-3 md:hidden">
        {items.length === 0 && (
          <div className="card-box p-4 text-gray-500">No UBOs yet.</div>
        )}
        {items.map((u) => {
          const liveChip = renderLivenessChip(u);
          return (
            <div key={u.id} className="card-box p-4 space-y-2">
              <div className="font-medium">
                {u.first_name} {u.last_name}
              </div>
              <div className="text-sm text-gray-600">
                <div>Birthdate: {fmtDate(u.birthdate as any)}</div>
                <div>Nationality: {u.nationality || "—"}</div>
                <div>Ownership: {u.ownership_percent}%</div>
                <div>Status: {u.status || "—"}</div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {u.require_id_reupload ? (
                    <span className="chip chip-warn">ID reupload requested</span>
                  ) : null}
                  {u.require_poa_reupload ? (
                    <span className="chip chip-warn">POA reupload requested</span>
                  ) : null}
                  {liveChip}
                </div>
              </div>
          <div className="pt-2 flex flex-wrap gap-2">
              {/* FIX: /profile/ubos/:id/edit */}
              <Link to={`/profile/ubos/${u.id}/edit`} className="btn">
                Edit / Manage KYC
              </Link>

              <a
                href={`/kyc/form?ubo_id=${u.id}&mobile=1`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-white"
              >
                Open KYC
              </a>
            </div>



              {(u.require_id_reupload || u.require_poa_reupload) && (
                <p className="text-xs text-gray-500 mt-1">
                  Document reupload is now done directly by the UBO in their own KYC
                  flow. Use “Edit / Manage KYC” to share or resend their KYC link.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop/tablet: full table */}
      <div className="hidden md:block bg-white rounded shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="table-header">
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Birthdate</th>
              <th className="px-3 py-2 text-left">Nationality</th>
              <th className="px-3 py-2 text-left">Ownership %</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Compliance</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-gray-500" colSpan={7}>
                  No UBOs yet.
                </td>
              </tr>
            )}
            {items.map((u) => {
              const liveChip = renderLivenessChip(u);
              return (
                <tr key={u.id} className="border-t">
                  <td className="px-3 py-2">
                    {u.first_name} {u.last_name}
                  </td>
                  <td className="px-3 py-2">{fmtDate(u.birthdate as any)}</td>
                  <td className="px-3 py-2">{u.nationality}</td>
                  <td className="px-3 py-2">{u.ownership_percent}%</td>
                  <td className="px-3 py-2">{u.status}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap gap-2">
                        {u.require_id_reupload ? (
                          <span className="chip chip-warn">
                            ID reupload requested
                          </span>
                        ) : null}
                        {u.require_poa_reupload ? (
                          <span className="chip chip-warn">
                            POA reupload requested
                          </span>
                        ) : null}
                      </div>
                      {liveChip && <div className="mt-1">{liveChip}</div>}
                    </div>
                  </td>
                  <td className="px-3 py-2 space-x-2 whitespace-nowrap">
                    {/* FIX: /profile/ubos/:id/edit */}
                    <Link to={`/profile/ubos/${u.id}/edit`} className="link">
                      Edit / Manage KYC
                    </Link>
                    {u.kyc_link && (
                      <a
                        href={u.kyc_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link"
                      >
                        Open KYC
                      </a>
                    )}
                    {(u.require_id_reupload || u.require_poa_reupload) && (
                      <div className="text-[11px] text-gray-500 mt-1">
                        Reupload is done by the UBO in their KYC. Use “Edit / Manage
                        KYC” to share the link.
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
